// ─── src/hooks/useInvestorLounge.ts ──────────────────────────────────────────

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteInvestorLoungeFounder,
  deleteInvestorLoungeInvestor,
  getInvestorLoungeFounders,
  getInvestorLoungeFounderStats,
  getInvestorLoungeInvestors,
  getInvestorLoungeInvestorStats,
  type InvestorLoungeFounderParams,
  type InvestorLoungeInvestorParams,
} from "../api/investorLounge";

// ── Founders ─────────────────────────────────────────────────────────────────

export const investorLoungeFounderKeys = {
  all:   ["investorLoungeFounders"] as const,
  list:  (p: InvestorLoungeFounderParams) =>
    [...investorLoungeFounderKeys.all, "list", p] as const,
  stats: () => [...investorLoungeFounderKeys.all, "stats"] as const,
};

export const useInvestorLoungeFounders = (params: InvestorLoungeFounderParams) =>
  useQuery({
    queryKey: investorLoungeFounderKeys.list(params),
    queryFn:  () => getInvestorLoungeFounders(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useInvestorLoungeFounderStats = () =>
  useQuery({
    queryKey: investorLoungeFounderKeys.stats(),
    queryFn: getInvestorLoungeFounderStats,
    staleTime: 60_000,
  });

export const useDeleteInvestorLoungeFounder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteInvestorLoungeFounder,
    onSuccess: () => qc.invalidateQueries({ queryKey: investorLoungeFounderKeys.all }),
  });
};

// ── Investors ────────────────────────────────────────────────────────────────

export const investorLoungeInvestorKeys = {
  all:   ["investorLoungeInvestors"] as const,
  list:  (p: InvestorLoungeInvestorParams) =>
    [...investorLoungeInvestorKeys.all, "list", p] as const,
  stats: () => [...investorLoungeInvestorKeys.all, "stats"] as const,
};

export const useInvestorLoungeInvestors = (params: InvestorLoungeInvestorParams) =>
  useQuery({
    queryKey: investorLoungeInvestorKeys.list(params),
    queryFn:  () => getInvestorLoungeInvestors(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useInvestorLoungeInvestorStats = () =>
  useQuery({
    queryKey: investorLoungeInvestorKeys.stats(),
    queryFn: getInvestorLoungeInvestorStats,
    staleTime: 60_000,
  });

export const useDeleteInvestorLoungeInvestor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteInvestorLoungeInvestor,
    onSuccess: () => qc.invalidateQueries({ queryKey: investorLoungeInvestorKeys.all }),
  });
};
